import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { distanceFromMeters, speedFromMs, tempFromC } from '../../../../shared/utils/unit-formatter.util';
import { formatLatLonDecimalMinutes } from '../../../../shared/utils/coordinate-format.util';

/**
 * Ride Along follow card — web equivalent of the Flutter TripStatusCard rendered
 * with `isRideAlong: true`. Presentational: it shows live stats for the captain's
 * trip the user is following (duration, speed, distance, water temp).
 *
 * The parent (home-page) owns the follow-mode polling and feeds this card its
 * inputs; the card only runs a 1s clock to tick the "Underway" duration.
 *
 * Units mirror the app exactly (see trip_status_card.dart):
 *   - speed  → always knots while riding along, regardless of the user's setting
 *   - distance / water temp → the user's profile unit preference
 */
@Component({
  selector: 'app-ride-along-follow',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ride-along-follow.component.html',
  styleUrl: './ride-along-follow.component.scss',
})
export class RideAlongFollowComponent implements OnInit, OnDestroy {
  /** Trip start time (ms epoch) used to tick the underway duration. */
  @Input() startTimeMs: number | null = null;
  /** Distance covered, in metres (SI in → formatted out, like the app). */
  @Input() distanceMeters: number | null = null;
  /** Latest speed over ground in metres/second. */
  @Input() speedMs: number | null = null;
  /** Latest water temperature in celsius. */
  @Input() waterTempC: number | null = null;
  /** Latest position (for the coordinates readout). */
  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  /** Captain paused the trip. */
  @Input() paused = false;
  /** User's profile unit preferences (distance_unit / temperature_unit). */
  @Input() distanceUnit: string | null = null;
  @Input() temperatureUnit: string | null = null;

  @Output() viewMates = new EventEmitter<void>();
  @Output() viewWaypoints = new EventEmitter<void>();

  duration = '00h 00m 00s';
  /** Compact (timer pill) by default; expands to full details like the app. */
  expanded = false;
  private intervalId: any;

  ngOnInit(): void {
    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  /**
   * Degrees + decimal minutes, via the shared formatter every other read-out
   * surface uses (Watch, Waypoints, Trips, the vessel snapshot).
   *
   * This card rendered its own degrees/minutes/SECONDS instead, so the same
   * position read `40°05'24.0"N` here and `40° 5.4' N` one tab over. The app has
   * no such split: `TripStatusCard._formatCoordinates` calls the same
   * `CoordinateFormatter` as everything else, and its default is DDM.
   */
  get coordinates(): string {
    if (this.latitude == null || this.longitude == null) return 'Acquiring Position…';
    return formatLatLonDecimalMinutes(this.latitude, this.longitude, 'Acquiring Position…');
  }

  get distanceLabel(): string {
    return distanceFromMeters(this.distanceMeters, this.distanceUnit, 2, '--').text;
  }

  /** Ride-along speed is always shown in knots — matches _formatSpeed in the app. */
  get speedLabel(): string {
    return speedFromMs(this.speedMs, 'knots', 1, '--').text;
  }

  get waterTempLabel(): string {
    return tempFromC(this.waterTempC, this.temperatureUnit, 1, '--').text;
  }

  onViewMates(): void {
    this.viewMates.emit();
  }

  onViewWaypoints(): void {
    this.viewWaypoints.emit();
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  private tick(): void {
    if (this.paused || this.startTimeMs == null) {
      if (this.startTimeMs == null) this.duration = '00h 00m 00s';
      return;
    }
    const diff = Math.max(0, Date.now() - this.startTimeMs);
    const totalSeconds = Math.floor(diff / 1000);
    const hh = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const mm = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const ss = (totalSeconds % 60).toString().padStart(2, '0');
    this.duration = `${hh}h ${mm}m ${ss}s`;
  }
}
